import { z } from 'zod';
import MailChecker from 'mailchecker';

// Error message shown when user tries to sign up with temporary or anonymized email
const BLOCKED_EMAIL_MESSAGE =
  'To help protect GearFocus and the community, please use your direct email address. Temporary and forwarding email services make it harder to keep the marketplace safe and to reach you if there\'s a problem with your order.';

// Additional disposable domains not covered by MailChecker
// Add domains here as we discover them
const ADDITIONAL_DISPOSABLE_DOMAINS = [
  'tempmail.com',
  'temp-mail.com',
  'tempmail.net',
  'tempmail.org',
  'tmpmail.org',
  'tmpmail.net',
  'throwaway.email',
  'throwawaymail.com',
  'tempinbox.com',
  'tempmailo.com',
  'tempmailaddress.com',
  'tempail.com',
  'tempr.email',
  'emailondeck.com',
  'getnada.com',
  'mohmal.com',
  'minutemail.com',
  'noomlocs.com',
  'okexbit.com',
  'dispostable.com',
  'maildrop.cc',
  'inboxkitten.com',
  'fakemail.net',
  'mailnesia.com',
  'emailfake.com',
  'crazymailing.com',
  'tempemailco.com',
  'usdtbeta.com',
  'burnermail.io',
];

// Email anonymizers and forwarding services
// These aren't necessarily fraud, but prevent us from reaching users directly
const EMAIL_ANONYMIZER_DOMAINS = [
  'privateid.com',
  'anonaddy.com',
  'simplelogin.io',
  'simplelogin.com',
  '33mail.com',
  'sneakemail.com',
  'spamgourmet.com',
  'blur.abine.com',
  'duck.com',           // DuckDuckGo email forwarding
  'relay.firefox.com',  // Firefox Relay
  'mozmail.com',        // Firefox Relay domain
];

/**
 * Check if an email is from a blocked email provider
 * Blocks: disposable emails, temporary emails, and anonymizer/forwarding services
 * Uses MailChecker library (33,000+ domains) plus our supplemental lists
 */
const isBlockedEmail = (email: string): boolean => {
  // First check MailChecker's extensive disposable email list
  if (!MailChecker.isValid(email)) {
    return true;
  }

  // Then check our supplemental lists for domains MailChecker misses
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;

  // Check disposable domains
  if (ADDITIONAL_DISPOSABLE_DOMAINS.includes(domain)) {
    return true;
  }

  // Check anonymizer/forwarding services
  if (EMAIL_ANONYMIZER_DOMAINS.includes(domain)) {
    return true;
  }

  return false;
};

export const emailSchema = z
  .string({ required_error: 'email is required' })
  .email('must enter a valid email')
  .refine(
    (email) => !isBlockedEmail(email),
    { message: BLOCKED_EMAIL_MESSAGE }
  );
